import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { Activity } from 'lucide-react';

const AXIS_TICK = { fontSize: 11 };
const TOOLTIP_CONTENT_STYLE = { background: '#0f172a', border: '1px solid #334155', borderRadius: 6 };
const TOOLTIP_LABEL_STYLE = { color: '#cbd5e1' };
const TOOLTIP_ITEM_STYLE = { color: '#fff' };
const LEGEND_STYLE = { color: '#cbd5e1', fontSize: 12 };
const CHART_CONTAINER_STYLE = { minHeight: 200 };

const fmtShortDay = (iso) => {
  try {
    return new Date(iso + 'T00:00:00Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

const TrafficList = ({ title, data, keyField, valueField = 'views' }) => {
  const entries = data || [];
  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-white">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-xs text-gray-500">No data yet.</p>
        ) : (
          <div className="space-y-2">
            {entries.map((row) => (
              <div key={row[keyField]} className="flex items-center justify-between text-sm">
                <span className="text-gray-300 truncate pr-3 font-mono text-xs">{row[keyField]}</span>
                <span className="text-red-400 font-semibold tabular-nums">{row[valueField]}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const TrafficSection = ({ summary }) => {
  const { totals, pageview_timeline, top_pages, top_referrers } = summary;
  const timelineData = (pageview_timeline || []).map((d) => ({ ...d, label: fmtShortDay(d.date) }));

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="traffic-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-white flex items-center gap-2">
          <Activity className="w-4 h-4 text-red-400" /> Site traffic · last 30 days
        </CardTitle>
        <p className="text-xs text-gray-500">
          {totals.pageviews ?? 0} page views · {totals.unique_visitors ?? 0} unique visitors
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="w-full min-w-0 h-[200px]" style={CHART_CONTAINER_STYLE}>
          <ResponsiveContainer width="100%" height={200} debounce={100}>
            <LineChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" stroke="#64748b" tick={AXIS_TICK} interval="preserveStartEnd" />
              <YAxis stroke="#64748b" tick={AXIS_TICK} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Line type="monotone" dataKey="views" name="Page views" stroke="#dc2626" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="visitors" name="Visitors" stroke="#60a5fa" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TrafficList title="Top pages" data={top_pages} keyField="path" />
          <TrafficList title="Top referrers" data={top_referrers} keyField="referrer" />
        </div>
      </CardContent>
    </Card>
  );
};

export default TrafficSection;
